import { medienArtNachName } from '@schnitt/shared';
import { api } from './api';
import { anDieReihe } from './einfuegeWarteschlange';
import { useEditor } from './store';
import { zeitText } from './zeit';

/**
 * **Dateien hochladen -- von überall her.** Bis zum 15. Oktober stand das im
 * Blatt „Medien“; seit Dateien auch mit der Maus auf die Zeitleiste fallen
 * (Sascha: „Drag&drop als Datei muss gehen!“), braucht es einen Ort, der
 * nicht an einem offenen Blatt hängt.
 *
 * Eine Datei nach der anderen, mit Absicht: ein Telefon, das vier Videos
 * zugleich über das Mobilnetz schickt, ist mit allen vieren spät fertig und
 * zeigt bis dahin nichts Brauchbares.
 *
 * **Hochladen und Wohin sind zwei Schritte** (Sascha, 4. November: „man weiß
 * nicht ganz, wann es wirklich hochgeladen wird … ich finde es kann direkt
 * nach der Auswahl schon anfangen“). `starteHochladen` legt sofort los, in
 * die Medien; `ablegen` sagt danach, ob und wo es in den Film kommt --
 * gleichgültig, ob das Hochladen dann schon fertig ist oder noch läuft.
 */
export interface Lauf {
  /** Die Dateien in der Reihenfolge, in der sie hochgeladen werden. */
  readonly dateien: readonly File[];
  /** Je Datei: die Kennung, `null` wenn nicht angekommen, `undefined` solange offen. */
  readonly ergebnis: (string | null | undefined)[];
  /** Welche Datei gerade hochlädt, und wie weit (0..1). */
  aktuell: number;
  anteil: number;
  /** Erfüllt, wenn alle durch sind -- mit dem Ergebnis je Datei. */
  readonly fertig: Promise<(string | null)[]>;
}

export function starteHochladen(dateien: readonly File[]): Lauf {
  const lauf: Lauf = {
    dateien: [...dateien],
    ergebnis: dateien.map(() => undefined),
    aktuell: 0,
    anteil: 0,
    fertig: Promise.resolve([]),
  };
  (lauf as { fertig: Promise<(string | null)[]> }).fertig = ladeHoch(lauf);
  return lauf;
}

async function ladeHoch(lauf: Lauf): Promise<(string | null)[]> {
  const s = useEditor.getState();
  const project = s.project;
  const gesamt = lauf.dateien.length;
  if (!project || gesamt === 0) return lauf.dateien.map(() => null);
  let abgebrochen = false;
  for (const [index, file] of lauf.dateien.entries()) {
    if (abgebrochen) {
      lauf.ergebnis[index] = null;
      continue;
    }
    lauf.aktuell = index;
    lauf.anteil = 0;
    s.setHochladen({ name: file.name, done: index, total: gesamt, fraction: 0 });
    try {
      const { asset } = await api.uploadAsset(project.id, file, (fraction) => {
        lauf.anteil = fraction;
        useEditor.getState().setHochladen({ name: file.name, done: index, total: gesamt, fraction });
      });
      lauf.ergebnis[index] = asset.id;
      // Gleich in die Liste: wer im Blatt „Medien“ steht, sieht die Datei
      // ankommen, nicht erst, wenn alle durch sind.
      void useEditor.getState().refreshAssets();
    } catch (err) {
      lauf.ergebnis[index] = null;
      s.toast('error', err instanceof Error ? err.message : `„${file.name}“ konnte nicht hochgeladen werden.`);
      abgebrochen = true;
    }
  }
  lauf.aktuell = gesamt;
  s.setHochladen(null);
  await useEditor.getState().refreshAssets();
  return lauf.ergebnis.map((e) => e ?? null);
}

/**
 * Wohin mit dem, was hochlädt oder hochgeladen ist: mit `kopf` in den Film,
 * ab dieser Stelle und in der Reihenfolge `reihe` (7.156), sobald es
 * aufbereitet ist; ohne bleibt es in den Medien. Zurück kommen die Kennungen
 * dessen, was angekommen ist.
 */
export async function ablegen(lauf: Lauf, reihe: readonly File[], kopf: number | null, spur: string | null = null): Promise<string[]> {
  const ergebnis = await lauf.fertig;
  const s = useEditor.getState();
  const ids = reihe
    .map((d) => ergebnis[lauf.dateien.indexOf(d)])
    .filter((x): x is string => typeof x === 'string');
  if (ids.length === 0) return [];
  const name = reihe.find((d) => ergebnis[lauf.dateien.indexOf(d)])?.name ?? '';
  if (kopf !== null) {
    // Erst in die Reihe, dann die Liste holen: ist eine Datei schon fertig,
    // kommt sie mit der neuen Liste sofort hinein.
    anDieReihe(ids, kopf, spur);
    await useEditor.getState().refreshAssets();
    const zeit = zeitText(kopf);
    s.toast(
      'info',
      ids.length === 1
        ? `„${name}“ ist hochgeladen und kommt bei ${zeit} in den Schnitt, sobald es aufbereitet ist.`
        : `${ids.length} Dateien sind hochgeladen und kommen ab ${zeit} in den Schnitt, sobald sie aufbereitet sind.`,
    );
    return ids;
  }
  s.toast('success', ids.length === 1 ? `„${name}“ ist hochgeladen und liegt in den Medien.` : `${ids.length} Dateien sind hochgeladen und liegen in den Medien.`);
  return ids;
}

/** Beides in einem: hochladen und gleich sagen, wohin (Ablegen mit der Maus). */
export async function hochladen(dateien: readonly File[], kopf: number | null, spur: string | null = null): Promise<string[]> {
  if (!useEditor.getState().project || dateien.length === 0) return [];
  return ablegen(starteHochladen(dateien), dateien, kopf, spur);
}

/**
 * Was beim Ablegen mitkommt und taugt: Video, Ton, Bild. Ein Ordner kommt
 * als Datei ohne Art und ohne Länge an, ein PDF mit seiner Art -- beides
 * bleibt draussen, und gesagt wird es auch.
 */
export function brauchbar(liste: FileList | readonly File[] | null | undefined): { gut: File[]; weg: string[] } {
  const gut: File[] = [];
  const weg: string[] = [];
  for (const datei of liste ? [...liste] : []) {
    const art = datei.type.split('/')[0];
    if (art === 'video' || art === 'audio' || art === 'image') gut.push(datei);
    // Nach dem Namen: manche Browser kennen die Art nicht (.mxf, .caf) oder
    // nennen eine andere (`application/ogg`). Ein Ordner hat weder Art noch
    // Inhalt und bleibt draussen.
    else if (datei.size > 0 && medienArtNachName(datei.name)) gut.push(datei);
    else weg.push(datei.name);
  }
  return { gut, weg };
}

/** Kommt beim Ziehen eine Datei mit -- und nicht ein Clip, ein Text oder ein Link? */
export function ziehtDateien(e: DragEvent | React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}
